import {
  collection,
  getDocs,
  doc,
  setDoc,
  updateDoc,
  Timestamp,
  writeBatch,
  query,
  where
} from 'firebase/firestore';
import { db } from '../firebase';

/**
 * UserService - Manages user data operations with caching and optimization
 *
 * Core principles:
 * - Minimize Firebase reads with aggressive caching
 * - Batch operations for efficiency
 * - Comprehensive logging for permission changes
 * - Optimistic UI updates
 */
class UserService {
  constructor() {
    this.cache = {
      users: null,
      timestamp: null,
      ttl: 30000 // 30 seconds cache
    };
  }

  /**
   * Get all users from Firestore with caching
   * Excludes parent-only users by default
   *
   * @param {boolean} forceRefresh - Force bypass cache
   * @returns {Promise<Array>} Array of user objects
   */
  async getAllUsers(forceRefresh = false, schoolId = null) {
    const now = Date.now();
    const cacheKey = schoolId || '_all';

    // Return cached data if still valid
    if (!forceRefresh && this.cache.users && this.cache.schoolId === cacheKey && (now - this.cache.timestamp) < this.cache.ttl) {
      console.log('[UserService] Returning cached users');
      return this.cache.users;
    }

    try {
      console.log('[UserService] Fetching users from Firestore', schoolId ? `for school ${schoolId}` : '(all)');
      const usersRef = collection(db, 'users');

      // Filter by school if provided — only show users belonging to this school
      let snapshot;
      if (schoolId) {
        // Query users whose schoolId or assignedSchoolId matches
        const bySchool = await getDocs(query(usersRef, where('schoolId', '==', schoolId)));
        const byAssigned = await getDocs(query(usersRef, where('assignedSchoolId', '==', schoolId)));

        // Merge and deduplicate
        const seen = new Set();
        const allDocs = [];
        for (const doc of [...bySchool.docs, ...byAssigned.docs]) {
          if (!seen.has(doc.id)) {
            seen.add(doc.id);
            allDocs.push(doc);
          }
        }
        snapshot = { docs: allDocs };
      } else {
        snapshot = await getDocs(usersRef);
      }

      const users = snapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        // Filter out parent-only users (users with no admin, dev, or staff flags)
        .filter(user => {
          // Dev users are always included regardless of school
          if (user.dev) return true;

          if (schoolId) {
            const schoolRole = user.schoolRoles?.[schoolId];
            const belongsToSchool = user.schoolId === schoolId
              || user.assignedSchoolId === schoolId
              || Boolean(schoolRole);
            const hasStaffRole = ['staff', 'teacher', 'admin'].includes(schoolRole?.accessRole)
              || schoolRole?.staff === true
              || schoolRole?.admin === true
              || user.admin === true
              || user.staff === true;
            return belongsToSchool && (hasStaffRole || (!user.parent && !user.parentOnly));
          }

          // Global query: include users with any school role entry
          const hasAnySchoolRole = Object.keys(user.schoolRoles || {}).length > 0;
          if (hasAnySchoolRole) return true;

          // Legacy fallback during migration window: global staff/admin flags
          if (user.admin || user.staff) return true;

          // Exclude parent-only users
          if (user.parent || user.parentOnly) return false;

          return true;
        })
        // Sort by email for consistent display
        .sort((a, b) => (a.email || '').localeCompare(b.email || ''));

      // Update cache
      this.cache.users = users;
      this.cache.timestamp = now;
      this.cache.schoolId = cacheKey;

      console.log(`[UserService] Fetched ${users.length} users (total in collection: ${snapshot.docs.length})`);
      return users;
    } catch (error) {
      console.error('[UserService] Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Update user role with validation and logging
   *
   * @param {string} userId - User ID to update
   * @param {Object} roleUpdate - Role update object: { accessRole, customRoleId } for school-scoped,
   *                              or legacy { dev } for platform-level dev flag
   * @param {string} updatedBy - ID of user making the change
   * @param {string|null} schoolId - School ID for school-scoped role (4th param)
   * @returns {Promise<boolean>} Success status
   */
  async updateUserRole(userId, roleUpdate, updatedBy, schoolId = null) {
    try {
      console.log(`[UserService] Updating role for user ${userId}:`, roleUpdate, schoolId ? `at school ${schoolId}` : '(global)');

      const userRef = doc(db, 'users', userId);

      if (schoolId) {
        // School-scoped role update — validate accessRole
        const { accessRole } = roleUpdate;
        if (accessRole !== undefined && accessRole !== 'staff' && accessRole !== 'admin') {
          throw new Error(`Invalid accessRole: "${accessRole}". Must be "staff" or "admin"`);
        }

        await updateDoc(userRef, {
          [`schoolRoles.${schoolId}`]: {
            ...roleUpdate,
            grantedAt: Timestamp.now(),
            grantedBy: updatedBy
          },
          updatedAt: Timestamp.now(),
          lastRoleUpdate: {
            timestamp: Timestamp.now(),
            updatedBy,
            changes: roleUpdate
          }
        });
      } else {
        // Legacy path — only dev flag allowed globally
        const validFields = ['dev', 'roleTemplateId'];
        const invalidFields = Object.keys(roleUpdate).filter(k => !validFields.includes(k));

        if (invalidFields.length > 0) {
          throw new Error(`Invalid role fields for global update: ${invalidFields.join(', ')}. Use schoolId parameter for school-scoped roles.`);
        }

        await updateDoc(userRef, {
          ...roleUpdate,
          updatedAt: Timestamp.now(),
          lastRoleUpdate: {
            timestamp: Timestamp.now(),
            updatedBy,
            changes: roleUpdate
          }
        });
      }

      // Invalidate cache
      this.invalidateCache();

      console.log(`[UserService] Successfully updated role for user ${userId}`);
      return true;
    } catch (error) {
      console.error(`[UserService] Error updating user role:`, error);
      throw error;
    }
  }

  /**
   * Update last login timestamp for a user
   *
   * @param {string} userId - User ID
   * @returns {Promise<boolean>} Success status
   */
  async updateLastLogin(userId) {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        lastLogin: Timestamp.now()
      });
      return true;
    } catch (error) {
      // Don't throw - this is non-critical
      console.warn('[UserService] Error updating last login:', error);
      return false;
    }
  }

  /**
   * Batch update multiple users
   *
   * @param {Array} updates - Array of {userId, roleUpdate} objects
   * @param {string} updatedBy - ID of user making changes
   * @returns {Promise<boolean>} Success status
   */
  async batchUpdateRoles(updates, updatedBy) {
    try {
      console.log(`[UserService] Batch updating ${updates.length} users`);

      const batch = writeBatch(db);
      const timestamp = Timestamp.now();

      updates.forEach(({ userId, roleUpdate }) => {
        const userRef = doc(db, 'users', userId);
        batch.update(userRef, {
          ...roleUpdate,
          updatedAt: timestamp,
          lastRoleUpdate: {
            timestamp,
            updatedBy,
            changes: roleUpdate
          }
        });
      });

      await batch.commit();

      // Invalidate cache
      this.invalidateCache();

      console.log('[UserService] Batch update completed successfully');
      return true;
    } catch (error) {
      console.error('[UserService] Error in batch update:', error);
      throw error;
    }
  }

  /**
   * Search users by email or display name
   *
   * @param {string} searchTerm - Search term
   * @param {Array} users - Optional user array to search (uses cache if not provided)
   * @returns {Promise<Array>} Filtered users
   */
  async searchUsers(searchTerm, users = null) {
    const allUsers = users || await this.getAllUsers();

    if (!searchTerm || searchTerm.trim() === '') {
      return allUsers;
    }

    const term = searchTerm.toLowerCase().trim();

    return allUsers.filter(user => {
      const email = (user.email || '').toLowerCase();
      const displayName = (user.displayName || '').toLowerCase();
      const name = (user.name || '').toLowerCase();

      return email.includes(term) ||
             displayName.includes(term) ||
             name.includes(term);
    });
  }

  /**
   * Get user role summary
   *
   * @param {Object} user - User object
   * @returns {string} Role display string
   */
  getUserRoleSummary(user) {
    const roles = [];

    if (user.dev) roles.push('Dev');
    if (user.admin) roles.push('Admin');
    if (user.staff) roles.push('Staff');

    return roles.length > 0 ? roles.join(', ') : 'Teacher';
  }

  /**
   * Get highest role for a user (for display)
   *
   * @param {Object} user - User object
   * @returns {string} Role identifier
   */
  getHighestRole(user) {
    if (user.dev) return 'dev';
    if (user.admin) return 'admin';
    if (user.staff) return 'staff';
    return 'teacher';
  }

  /**
   * Get users belonging to a specific school
   *
   * @param {string} schoolId - School ID to filter users
   * @returns {Promise<Array>} Array of user objects for that school
   */
  async getSchoolUsers(schoolId) {
    if (!schoolId) return [];

    try {
      console.log(`[UserService] Fetching users for school ${schoolId}`);
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('assignedSchoolId', '==', schoolId));
      const snapshot = await getDocs(q);

      const users = snapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(user => !user.deleted && user.active !== false)
        .filter(user => user.admin || user.dev || user.staff || !user.parent)
        .sort((a, b) => (a.displayName || a.email || '').localeCompare(b.displayName || b.email || ''));

      console.log(`[UserService] Found ${users.length} users for school ${schoolId}`);
      return users;
    } catch (error) {
      console.error('[UserService] Error fetching school users:', error);
      throw error;
    }
  }

  /**
   * Invalidate the user cache
   */
  invalidateCache() {
    this.cache.users = null;
    this.cache.timestamp = null;
    console.log('[UserService] Cache invalidated');
  }

  /**
   * Clear all caches
   */
  clearCache() {
    this.invalidateCache();
  }

  /**
   * Get parent users with enriched data (Telegram status, children count)
   *
   * @param {string} schoolId - School ID to filter parents
   * @returns {Promise<Array>} Array of parent objects with enriched data
   */
  async getParentUsers(schoolId) {
    try {
      // Parent identities can use either the active or assigned school field.
      const usersRef = collection(db, 'users');
      const [bySchool, byAssigned] = await Promise.all([
        getDocs(query(usersRef, where('parent', '==', true), where('schoolId', '==', schoolId))),
        getDocs(query(usersRef, where('parent', '==', true), where('assignedSchoolId', '==', schoolId))),
      ]);
      const parentDocs = new Map();
      [...bySchool.docs, ...byAssigned.docs].forEach((parentDoc) => parentDocs.set(parentDoc.id, parentDoc));
      const parents = [...parentDocs.values()].map(parentDoc => ({
        id: parentDoc.id,
        ...parentDoc.data(),
      }));

      // One school-scoped link query and one roster query replace the previous
      // one-query-per-parent loop while retaining learner names for legacy links.
      const [linksSnapshot, rosterSnapshot] = parents.length > 0
        ? await Promise.all([
          getDocs(query(collection(db, 'parentChildLinks'), where('schoolId', '==', schoolId))),
          getDocs(collection(db, `schools/${schoolId}/students`)),
        ])
        : [{ docs: [] }, { docs: [] }];
      const studentNameById = new Map(rosterSnapshot.docs.map((studentDoc) => {
        const student = studentDoc.data();
        const studentName = [student.firstName, student.surname || student.lastName]
          .filter(Boolean)
          .join(' ')
          || student.displayName
          || student.name;
        return [studentDoc.id, studentName || ''];
      }));
      const linksByParent = new Map();
      linksSnapshot.docs.forEach((linkDoc) => {
        const link = { id: linkDoc.id, ...linkDoc.data() };
        if (!link.parentId || !parentDocs.has(link.parentId)) return;
        const links = linksByParent.get(link.parentId) || [];
        links.push(link);
        linksByParent.set(link.parentId, links);
      });

      const enrichedParents = parents.map((parent) => {
        const links = linksByParent.get(parent.id) || [];
        const verifiedLinks = links.filter(link => link.telegram?.verified && link.telegram?.chatId);
        const linkedStudentIds = Array.isArray(parent.linkedStudentIds) ? parent.linkedStudentIds : [];
        const children = links.length > 0
          ? links.map(link => ({
            studentId: link.studentId,
            studentName: studentNameById.get(link.studentId) || link.studentName || 'Unknown',
            relationship: link.relationship?.type || 'PARENT',
            telegramVerified: link.telegram?.verified || false,
          }))
          : linkedStudentIds.map(studentId => ({
            studentId,
            studentName: studentNameById.get(studentId) || 'Unknown',
            relationship: 'PARENT',
            telegramVerified: false,
          }));

        return {
          ...parent,
          childrenCount: children.length,
          hasTelegram: verifiedLinks.length > 0,
          telegramVerified: verifiedLinks.length,
          telegramTotal: links.length,
          children,
        };
      });

      console.log(`[UserService] Enriched ${enrichedParents.length} parent users`);
      return enrichedParents;
    } catch (error) {
      console.error('[UserService] Error fetching parent users:', error);
      throw error;
    }
  }

  /**
   * Create a new user (invitation/manual entry)
   *
   * @param {Object} userData - User data
   * @param {string} createdBy - ID of user creating this user
   * @returns {Promise<string>} Created user ID
   */
  async createUser(userData, createdBy) {
    try {
      console.log(`[UserService] Creating new user:`, userData);

      // Generate a unique ID for the user (will be replaced with auth UID on signup)
      const userRef = doc(collection(db, 'users'));
      const userId = userRef.id;

      // Create user document
      const userDoc = {
        email: userData.email,
        displayName: userData.displayName || '',
        // Role flags
        admin: userData.admin || false,
        staff: userData.staff || false,
        dev: userData.dev || false,
        parent: userData.parent || false,
        // Status
        pending: true, // Mark as pending until they complete signup
        active: true,
        // School assignment
        schoolId: userData.schoolId || null,
        assignedSchoolId: userData.schoolId || null,
        schoolIds: userData.schoolId ? [userData.schoolId] : [],
        // Metadata
        createdAt: Timestamp.now(),
        createdBy,
        updatedAt: Timestamp.now()
      };

      await setDoc(userRef, userDoc);

      // Invalidate cache
      this.invalidateCache();

      console.log(`[UserService] Successfully created user ${userId}`);
      return userId;
    } catch (error) {
      console.error(`[UserService] Error creating user:`, error);
      throw error;
    }
  }

  /**
   * Delete a user and cleanup related data
   *
   * @param {string} userId - User ID to delete
   * @param {string} deletedBy - ID of user performing deletion
   * @returns {Promise<boolean>} Success status
   */
  async deleteUser(userId, deletedBy) {
    try {
      console.log(`[UserService] Deleting user ${userId} by ${deletedBy}`);

      // Note: This only deletes the user document
      // Related data (parentChildLinks, etc.) should be handled by Cloud Functions
      // or as a separate cleanup operation based on your data retention policies

      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        deleted: true,
        deletedAt: Timestamp.now(),
        deletedBy,
        active: false
      });

      // Invalidate cache
      this.invalidateCache();

      console.log(`[UserService] Successfully marked user ${userId} as deleted`);
      return true;
    } catch (error) {
      console.error(`[UserService] Error deleting user:`, error);
      throw error;
    }
  }

  /**
   * Update user profile information
   *
   * @param {string} userId - User ID
   * @param {Object} updates - Profile fields to update (displayName, email, photoURL, etc.)
   * @param {string} updatedBy - ID of user making the change
   * @returns {Promise<boolean>} Success status
   */
  async updateUserProfile(userId, updates, updatedBy) {
    try {
      console.log(`[UserService] Updating profile for user ${userId}:`, updates);

      const userRef = doc(db, 'users', userId);

      await updateDoc(userRef, {
        ...updates,
        updatedAt: Timestamp.now(),
        lastProfileUpdate: {
          timestamp: Timestamp.now(),
          updatedBy,
          changes: updates
        }
      });

      // Invalidate cache
      this.invalidateCache();

      console.log(`[UserService] Successfully updated profile for user ${userId}`);
      return true;
    } catch (error) {
      console.error(`[UserService] Error updating user profile:`, error);
      throw error;
    }
  }
}

// Export singleton instance
export const userService = new UserService();
export default userService;
